import { useState, useCallback, useMemo } from 'react';

export interface ModalState<T extends Record<string, any> = any> {
  isOpen: boolean;
  open: (options?: T) => void;
  close: () => void;
  options: T | null;
  resetOptions: () => void;
}

const useModalState = <T extends Record<string, any> = any>(
  open = false
): ModalState<T> => {
  const [modalOpen, setModalOpen] = useState(open);
  const [options, setOptions] = useState<T | null>(null);

  const handleModalOpen = useCallback((newOptions?: T) => {
    setModalOpen(true);

    if (newOptions) {
      setOptions(newOptions);
    }
  }, []);

  const handleModalClose = useCallback(() => {
    setModalOpen(false);

    if (!options) {
      setOptions(null);
    }
  }, [options]);

  const handleResetOptions = useCallback(() => {
    setOptions(null);
  }, []);

  return useMemo(
    () => ({
      isOpen: modalOpen,
      open: handleModalOpen,
      close: handleModalClose,
      options,
      resetOptions: handleResetOptions,
    }),
    [handleModalClose, handleModalOpen, modalOpen, options, handleResetOptions]
  );
};

export default useModalState;
